import { type ResponseContext } from '@linktivity/ars-neo';
import {
  UNAUTHORIZED,
  TOO_MANY_REQUESTS,
  INTERNAL_SERVER_ERROR,
  AGENT_ID
} from '@link/share/constants';
import {
  createBuildRequestHeaders,
  createHandleGenericError,
  createHandleInternalServerError,
  createHandleTooManyRequests,
  createHandleUnauthorized,
  createPreMiddleware
} from '@link/share/services/config';
import i18n from '@link/shibuya-sky/i18n';
import { auth } from '@link/shibuya-sky/stores';

const BASE_PATH = import.meta.env.VITE_APP_ARS_API as string;

export interface ServiceConfigOptions<T, C = unknown> {
  Configuration: new (config: C) => T;
  basePath?: string;
}

const buildRequestHeaders = createBuildRequestHeaders({
  getAccessToken: () => auth.getAccessToken(),
  agentId: AGENT_ID
});

const preMiddleware = createPreMiddleware(() => auth, buildRequestHeaders);

const handleTooManyRequests = createHandleTooManyRequests(i18n);

const handleInternalServerError = createHandleInternalServerError(i18n);

const handleUnauthorized = createHandleUnauthorized(() => auth);

const handleGenericError = createHandleGenericError(i18n);

// Grpc-Metadata-Agent
// Grpc-Metadata-Authorization
export function createServiceConfig<T, C = unknown>({
  Configuration,
  basePath = BASE_PATH
}: ServiceConfigOptions<T, C>) {
  return new Configuration({
    basePath,
    middleware: [
      {
        pre: preMiddleware,
        post: async (context: ResponseContext) => {
          if (context.response.ok) {
            return;
          }
          if (context.response.status === UNAUTHORIZED) {
            return handleUnauthorized(context);
          }
          if (context.response.status === TOO_MANY_REQUESTS) {
            return handleTooManyRequests(context);
          }
          if (context.response.status === INTERNAL_SERVER_ERROR) {
            return handleInternalServerError(context);
          }
          return handleGenericError(context);
        }
      }
    ]
  } as C);
}
